import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Server, 
  BarChart3, 
  Zap,
  Shield,
  Cloud,
  Database,
  Monitor,
  Settings,
  HeadphonesIcon,
  CheckCircle2,
  Clock,
  TrendingUp
} from 'lucide-react';

const mainServices = [
  {
    icon: Server,
    title: 'Managed IT Services',
    description: 'Comprehensive technology support for your business through proactive monitoring, maintenance, and management of your IT infrastructure.',
    features: [
      'Network security management',
      'Cloud services & integration',
      'Help desk support',
      'Strategic IT planning',
      'Hardware & software management',
      'Backup & disaster recovery'
    ],
    image: 'https://images.pexels.com/photos/1181320/pexels-photo-1181320.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-cyan-500 to-blue-600',
    bgGradient: 'from-cyan-50 to-blue-50'
  },
  {
    icon: BarChart3,
    title: 'IT Consulting',
    description: 'Expert consultants analyze your unique needs, recommend strategic solutions, and guide implementation to drive growth and efficiency.',
    features: [
      'Digital transformation strategy',
      'Cybersecurity planning',
      'Technology roadmap development',
      'Process optimization',
      'Vendor management',
      'IT budget planning'
    ],
    image: 'https://images.pexels.com/photos/6170758/pexels-photo-6170758.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-fuchsia-500 to-purple-600',
    bgGradient: 'from-fuchsia-50 to-purple-50'
  },
  {
    icon: Zap,
    title: 'Project Management',
    description: 'Efficiently plan, execute, and monitor IT projects, ensuring timely delivery and optimal resource utilization.',
    features: [
      'Agile project management',
      'Resource allocation',
      'Risk assessment & mitigation',
      'Stakeholder communication',
      'Quality assurance',
      'Post-project support'
    ],
    image: 'https://images.pexels.com/photos/7439127/pexels-photo-7439127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    gradient: 'from-orange-500 to-amber-500',
    bgGradient: 'from-orange-50 to-amber-50'
  }
];

const additionalServices = [
  { icon: Shield, title: 'Cybersecurity', description: 'Protect your business from threats' },
  { icon: Cloud, title: 'Cloud Migration', description: 'Seamless transition to the cloud' },
  { icon: Database, title: 'Data Analytics', description: 'Power BI consulting & insights' },
  { icon: Monitor, title: 'System Monitoring', description: '24/7 infrastructure monitoring' },
  { icon: Settings, title: 'Automation', description: 'Streamline repetitive tasks' },
  { icon: HeadphonesIcon, title: 'Help Desk', description: 'Responsive technical support' }
];

const benefits = [
  { icon: Clock, title: '99.9% Uptime', description: 'Guaranteed system availability' },
  { icon: TrendingUp, title: 'Cost Savings', description: 'Reduce IT overhead by 30%' },
  { icon: Shield, title: 'Enhanced Security', description: 'Enterprise-grade protection' },
  { icon: HeadphonesIcon, title: '24/7 Support', description: 'Always here when you need us' }
];

export const Services = () => {
  return (
    <div className="overflow-hidden" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-700 rounded-full text-sm font-semibold mb-4">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Comprehensive <span className="gradient-text-alt">IT Solutions</span>
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From managed IT services to strategic consulting, we provide everything 
              your business needs to thrive in the digital age.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {mainServices.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`grid lg:grid-cols-2 gap-16 items-center mb-32 last:mb-0 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`main-service-${index}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} mb-6`}>
                  <service.icon className="text-white" size={32} />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">{service.title}</h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">{service.description}</p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className={`flex-shrink-0 ${
                        service.gradient.includes('cyan') ? 'text-cyan-500' :
                        service.gradient.includes('fuchsia') ? 'text-fuchsia-500' :
                        'text-orange-500'
                      }`} size={18} />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  className={`group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${service.gradient} text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300`}
                >
                  Get Started <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className={`absolute inset-0 bg-gradient-to-r ${service.gradient} rounded-3xl blur-2xl opacity-20`} />
                <img
                  src={service.image}
                  alt={service.title}
                  className="relative rounded-3xl shadow-2xl w-full"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Services Grid */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
              More Solutions
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Additional <span className="gradient-text">Services</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Specialized solutions to address your specific IT challenges
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="p-4 rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 group-hover:from-cyan-100 group-hover:to-blue-50 transition-colors duration-300 inline-block mb-4">
                  <service.icon className="text-slate-600 group-hover:text-cyan-600 transition-colors duration-300" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Why Partner with <span className="gradient-text">ZenithForge</span>?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Experience the benefits of working with a dedicated IT partner
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 mb-4">
                  <benefit.icon className="text-cyan-400" size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              How We <span className="gradient-text">Work</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discover', description: 'We analyze your current IT landscape and understand your goals' },
              { step: '02', title: 'Strategize', description: 'We develop a tailored IT strategy aligned with your business' },
              { step: '03', title: 'Implement', description: 'We deploy solutions with minimal disruption to your operations' },
              { step: '04', title: 'Support', description: 'We provide ongoing support and continuous optimization' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-6xl font-bold gradient-text mb-4">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500 to-transparent -translate-x-1/2" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 cta-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Get <span className="gradient-text">Started</span>?
            </h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Let's discuss how our services can help your business achieve its technology goals.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-slate-900 font-bold rounded-full hover:shadow-2xl hover:shadow-white/20 transition-all duration-300"
              data-testid="services-cta"
            >
              Contact Us Today
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
